import React, { useMemo } from "react";
import { apiService } from "../../../../../api/api";
import { ROUTES } from "../../../../../config/routes";
import { WidgetComponentProps, emailsInScope, getScope, matchesEmails } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import {
  CardError,
  CardLoading,
  Kpi,
  KpiGrid,
  ListRows,
  countTone,
  formatNumber,
  formatRelative,
} from "../../ui";
import { toTime, withinLastDays } from "./cardHelpers";

type ScrapeJob = Awaited<ReturnType<typeof apiService.scrapers.getScrapeJobs>>[number];

const RECENT_DAYS = 7;

const finishedAt = (job: ScrapeJob) => job.completedAt || job.createdAt;

const ScrapersCard: React.FC<WidgetComponentProps> = ({ access, filters }) => {
  const { data, loading, error, reload } = useWidgetData<ScrapeJob[]>("scrapers", () =>
    apiService.scrapers.getScrapeJobs()
  );

  const allowed = emailsInScope(filters, access.email);
  const mine = getScope(filters) === "mine";

  const stats = useMemo(() => {
    const jobs = (Array.isArray(data) ? data : []).filter(
      (j) => !j.isDeleted && matchesEmails(allowed, j.createdBy ? [j.createdBy] : [])
    );
    const recent = [...jobs]
      .sort((a, b) => (toTime(b.createdAt) ?? 0) - (toTime(a.createdAt) ?? 0))
      .slice(0, 3);
    return {
      total: jobs.length,
      running: jobs.filter((j) => j.status === "running" || j.status === "queued").length,
      failed: jobs.filter((j) => j.status === "failed" && withinLastDays(finishedAt(j), RECENT_DAYS)).length,
      completed: jobs.filter((j) => j.status === "completed" && withinLastDays(finishedAt(j), RECENT_DAYS))
        .length,
      recent,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(allowed)]);

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const link = ROUTES.MUSIC_ASSET_SCRAPER;

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label="Running"
          value={stats.running}
          tone={stats.running > 0 ? "info" : "neutral"}
          hint="Incl. queued"
          to={link}
          emphasis
        />
        <Kpi
          label={`Failed (${RECENT_DAYS}d)`}
          value={stats.failed}
          tone={countTone(stats.failed, "bad")}
          to={link}
        />
        <Kpi
          label={`Completed (${RECENT_DAYS}d)`}
          value={stats.completed}
          tone={stats.completed > 0 ? "good" : "neutral"}
          to={link}
        />
      </KpiGrid>

      <ListRows
        title={mine ? "Your recent jobs" : "Recent jobs"}
        emptyText={mine ? "You haven't run any scrape jobs" : "No scrape jobs yet"}
        rows={stats.recent.map((j) => ({
          key: j.id,
          primary: (
            <span className={j.status === "failed" ? "home-text-bad" : undefined}>
              {j.title || j.brand || "Untitled job"}
            </span>
          ),
          secondary: `${j.status} · ${formatNumber(j.assetCount ?? 0)} assets · ${
            formatRelative(j.createdAt) ?? "–"
          }`,
          to: link,
        }))}
      />
    </>
  );
};

export default ScrapersCard;
